import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Divider } from "@mui/material";

import { ServiceRequest } from "../../../types/service.type";
import { useServiceStatusFlags } from "../../../hooks/useServiceStatusFlags";
import { useServiceRequestActions } from "../../../hooks/useServiceRequestActions";
import { getServicePageRoute } from "../../../../../routers/routeUtils";
import { ServiceTypeEnum } from "../../../enum/service.enum";

interface ProviderActionsProps {
  serviceRequest: ServiceRequest;
  isContractPaid: boolean;
  onOpenModal: (config: { title: string; subTitle?: string; onConfirm: () => void }) => void;
}

export const ProviderActions = ({ serviceRequest, isContractPaid, onOpenModal }: ProviderActionsProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });
  const navigate = useNavigate();
  const { hasContract, isCompleted, isCanceled, isRejected, isNegotiated, isContracted } =
    useServiceStatusFlags(serviceRequest);

  const { handleAcceptContract, handleEndContract, handleProviderCancel, isResponding, isCancellingByProvider } =
    useServiceRequestActions(serviceRequest);

  const handleViewServiceDetails = () => {
    if (!serviceRequest.service.id) return;
    const serviceDetailsRoute = getServicePageRoute(serviceRequest.service.id, ServiceTypeEnum.OneTime);
    navigate(serviceDetailsRoute);
  };

  const handleEndContractWithModal = () => {
    onOpenModal({
      title: isContractPaid ? t("areYouSureToEndContract") : t("areYouSureToRejectThisContract"),
      subTitle: isContractPaid
        ? t("onceEndedThisContractThisActionCannotBeUndone")
        : t("onceRejectedThisContractThisActionCannotBeUndone"),
      onConfirm: handleEndContract,
    });
  };

  const handleRejectContractWithModal = () => {
    onOpenModal({
      title: t("areYouSureToRejectThisContract"),
      subTitle: t("onceRejectedThisContractThisActionCannotBeUndone"),
      onConfirm: handleEndContract,
    });
  };

  const handleProviderCancelWithModal = () => {
    onOpenModal({
      title: t("areYouSureToRejectThisContract"),
      subTitle: t("onceRejectedThisContractThisActionCannotBeUndone"),
      onConfirm: handleProviderCancel,
    });
  };

  if (isRejected || isCanceled || isCompleted) return null;

  // to be deleted? or it's a fallback due to the service can't be rejected?
  if (!hasContract) {
    return (
      <>
        <Divider />
        <Box sx={styles.container}>
          <Button variant="primary-md" onClick={handleViewServiceDetails}>
            {t("viewServiceDetails")}
          </Button>
          <Button variant="outlined-md" onClick={handleProviderCancelWithModal} disabled={isCancellingByProvider}>
            {/* {t("rejectService")} */}
            {t("rejectRequest")}
          </Button>
        </Box>
      </>
    );
  }

  if (isContracted) {
    return (
      <>
        <Divider />
        <Button variant="outlined-sm" onClick={handleEndContractWithModal} disabled={isResponding}>
          {isContractPaid ? t("endContract") : t("withdrawApproval")}
        </Button>
      </>
    );
  }

  if (isNegotiated) {
    return (
      <>
        <Divider />
        <Box sx={styles.container}>
          <Button variant="primary-md" onClick={handleAcceptContract} disabled={isResponding}>
            {t("acceptContract")}
          </Button>
          <Button variant="outlined-sm" onClick={handleRejectContractWithModal} disabled={isResponding}>
            {t("rejectContract")}
          </Button>
        </Box>
      </>
    );
  }

  return null;
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
};
